import { useCallback, useEffect, useRef, useState } from 'react';
import { ScanSearch, Check, AlertTriangle, Plus, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { FotoProduto } from '../../components/FotoProduto';
import { ComboBox, type OpcaoCombo } from '../../components/ComboBox';
import { formatDataSP, formatHoraSP } from '@fiodecobre/shared';
import { BuscaProdutoEstoque, type ProdutoBuscado } from './BuscaProdutoEstoque';

// AUDITORIA DO ESTOQUE ([DA-868]) — a tela da gerente (SHIRLEY) e do admin.
//
// Ela e' o JUIZ do Premio do Estoquista. Por isso duas regras mandam no desenho:
//   1. **Contagem CEGA.** A tela mostra o produto e ONDE procurar, nunca QUANTO o sistema espera.
//      Quem ve o numero tende a "achar" o numero. O servidor nem manda a quantidade das pendentes.
//   2. **Corrige na hora** (decisao dele). Nao bateu, o servidor corrige o estoque e as prateleiras
//      no mesmo clique, pela mesma peca da Conferencia — a tela so mostra o desfecho.
//
// O estoquista nao alcanca esta tela (menu e rotas so admin/gerente): se visse o sorteio, contaria
// primeiro os sorteados e a nota mediria a lista, nao o estoque.

type LugarCego = { localizacao_id: number; codigo: string; descricao: string | null; principal: boolean };

type ItemAuditoria = {
  id: number;
  data: string;
  origem: 'sorteio' | 'avulsa';
  resultado: 'certo' | 'errado' | null;
  erro_quantidade: boolean | null;
  erro_lugar: boolean | null;
  estoque_sistema: string | null;
  estoque_contado: string | null;
  lugares_sistema: { localizacao_id: number; codigo: string; quantidade: number | null }[] | null;
  lugares_contados: { localizacao_id: number; codigo: string; quantidade: number }[] | null;
  avaliado_em: string | null;
  auditor: string | null;
  produto: { id: number; codigo_interno: string | null; descricao: string; foto_url: string | null; unidade: string };
  lugares: LugarCego[];
};

type Desfecho = {
  resultado: 'certo' | 'errado';
  erro_quantidade: boolean;
  erro_lugar: boolean;
  estoque_sistema: number;
  estoque_contado: number;
};

const fmt = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : String(v);
};
const num = (s: string) => Number(String(s).replace(',', '.'));

export default function AuditoriaEstoque() {
  const [aba, setAba] = useState<'sorteio' | 'achado'>('sorteio');
  const [itens, setItens] = useState<ItemAuditoria[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<number | null>(null);
  const [opcoesLugares, setOpcoesLugares] = useState<OpcaoCombo[]>([]);
  const reqId = useRef(0);

  const carregar = useCallback(async () => {
    const req = ++reqId.current;
    setCarregando(true);
    try {
      const r = await api.get<{ itens: ItemAuditoria[] }>('/api/estoque/auditoria/hoje');
      if (req !== reqId.current) return;
      setItens(r.itens);
      setErro(null);
    } catch (e: any) {
      if (req === reqId.current) setErro(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel carregar o sorteio.');
    } finally {
      if (req === reqId.current) setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);
  useEffect(() => {
    api.get<{ id: number; codigo: string; descricao: string | null }[]>('/api/localizacoes')
      .then((l) => setOpcoesLugares(l.map((x) => ({ value: x.id, label: x.descricao ? `${x.codigo} — ${x.descricao}` : x.codigo }))))
      .catch(() => setOpcoesLugares([]));
  }, []);

  const pendentes = itens.filter((i) => i.resultado === null).length;
  const sorteados = itens.filter((i) => i.origem === 'sorteio');

  return (
    <div className="pagina">
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <ScanSearch size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Auditoria do Estoque
        </h2>
        <div className="pagina-acoes" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'inline-flex', border: '1px solid var(--panel-border)', borderRadius: 6, overflow: 'hidden' }}>
            {([['sorteio', 'Sorteio de hoje'], ['achado', 'Registrar achado']] as const).map(([id, rot]) => (
              <button
                key={id}
                type="button"
                onClick={() => setAba(id)}
                aria-pressed={aba === id}
                style={{
                  padding: '8px 14px', fontSize: 14, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                  background: aba === id ? 'var(--primary)' : 'transparent',
                  color: aba === id ? '#fff' : 'inherit',
                  fontWeight: aba === id ? 700 : 400,
                }}
              >
                {rot}
              </button>
            ))}
          </div>
          {aba === 'sorteio' && !carregando && (
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {pendentes === 0 ? 'Tudo contado' : pendentes === 1 ? '1 pra contar' : `${pendentes} pra contar`}
            </span>
          )}
        </div>
      </div>

      {erro && (
        <div style={{ background: '#f97316', color: '#000', padding: 12, borderRadius: 6, marginBottom: 12, fontWeight: 600 }}>{erro}</div>
      )}

      {aba === 'sorteio' ? (
        <>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8, maxWidth: 860 }}>
            Produtos sorteados hoje. Va ate cada prateleira, conte e digite o que achou — a tela nao mostra o numero do
            sistema de proposito. Se achar o produto em outro lugar, acrescente o lugar. O que nao bater e' corrigido na hora.
          </div>
          {carregando && <div className="muted" style={{ padding: 16 }}>Carregando o sorteio...</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 860 }}>
            {sorteados.map((item) => (
              <CardAuditoria
                key={item.id}
                item={item}
                aberto={aberto === item.id}
                onAbrir={() => setAberto(item.id)}
                onFechar={() => setAberto(null)}
                opcoesLugares={opcoesLugares}
                onAvaliado={() => { setAberto(null); void carregar(); }}
              />
            ))}
          </div>
        </>
      ) : (
        <Achado opcoesLugares={opcoesLugares} />
      )}
    </div>
  );
}

function Cabecalho({ produto, lugaresTexto, extra }: {
  produto: ItemAuditoria['produto'] | ProdutoBuscado; lugaresTexto: string; extra?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      {produto.foto_url
        ? <FotoProduto url={produto.foto_url} tam={56} />
        : <div className="produto-thumb-vazio" style={{ width: 56, height: 56, flex: '0 0 auto' }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-gray-500 font-bold no-underline" style={{ fontSize: 12 }}>{produto.codigo_interno}</div>
        <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.2 }}>{produto.descricao}</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>
          <strong>{lugaresTexto}</strong>
          <span className="muted"> · {produto.unidade}</span>
        </div>
      </div>
      {extra}
    </div>
  );
}

function CardAuditoria({ item, aberto, onAbrir, onFechar, opcoesLugares, onAvaliado }: {
  item: ItemAuditoria;
  aberto: boolean;
  onAbrir: () => void;
  onFechar: () => void;
  opcoesLugares: OpcaoCombo[];
  onAvaliado: () => void;
}) {
  const dialog = useDialog();
  const feito = item.resultado !== null;
  const lugaresTexto = feito
    ? (item.lugares_contados ?? []).map((l) => l.codigo).join(' · ') || 'sem prateleira'
    : item.lugares.map((l) => `${l.codigo}${l.principal ? ' ★' : ''}`).join(' · ') || 'sem prateleira cadastrada';

  async function enviar(contados: { localizacao_id: number; quantidade: number }[]) {
    try {
      const r = await api.post<Desfecho>(`/api/estoque/auditoria/${item.id}/avaliar`, { contados });
      dialog.toast(r.resultado === 'certo' ? `${item.produto.codigo_interno ?? ''} certo` : `${item.produto.codigo_interno ?? ''} corrigido`);
      onAvaliado();
    } catch (e: any) {
      await dialog.alerta(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel gravar a contagem.', { titulo: 'Nao gravou', tipo: 'aviso' });
    }
  }

  return (
    <div style={{
      border: '1px solid var(--panel-border)', borderRadius: 6, padding: 12, background: 'var(--panel-bg)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <Cabecalho
        produto={item.produto}
        lugaresTexto={lugaresTexto}
        extra={feito ? <SeloDesfecho resultado={item.resultado!} /> : null}
      />
      {feito ? (
        <ResumoDesfecho
          erroQtd={!!item.erro_quantidade}
          erroLugar={!!item.erro_lugar}
          sistema={item.estoque_sistema}
          contado={item.estoque_contado}
          rodape={`${item.auditor ?? ''} ${item.avaliado_em ? `as ${formatHoraSP(item.avaliado_em)}` : ''}${item.data ? ` · sorteio de ${formatDataSP(`${item.data}T12:00:00-03:00`)}` : ''}`}
        />
      ) : aberto ? (
        <ContagemCega lugares={item.lugares} opcoesLugares={opcoesLugares} onCancelar={onFechar} onEnviar={enviar} />
      ) : (
        <button
          type="button"
          onClick={onAbrir}
          style={{
            padding: '14px 0', fontSize: 16, fontWeight: 700, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: 'var(--primary)', color: '#fff', whiteSpace: 'nowrap',
          }}
        >
          Contar
        </button>
      )}
    </div>
  );
}

function SeloDesfecho({ resultado }: { resultado: 'certo' | 'errado' }) {
  // Amarelo + preto = positivo; laranja + preto = atencao. Nunca verde/vermelho (daltonismo dele).
  return (
    <span style={{
      background: resultado === 'certo' ? '#fde68a' : '#f97316', color: '#000', fontWeight: 700,
      padding: '4px 10px', borderRadius: 6, fontSize: 13, whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      {resultado === 'certo' ? <><Check size={14} /> Certo</> : <><AlertTriangle size={14} /> Corrigido</>}
    </span>
  );
}

function ResumoDesfecho({ erroQtd, erroLugar, sistema, contado, rodape }: {
  erroQtd: boolean; erroLugar: boolean; sistema: string | number | null; contado: string | number | null; rodape?: string;
}) {
  const partes: string[] = [];
  if (erroQtd) partes.push(`o sistema dizia ${fmt(sistema)} e havia ${fmt(contado)}`);
  if (erroLugar) partes.push(erroQtd ? 'e nao estava onde o sistema apontava' : 'o total batia, mas nao estava onde o sistema apontava');
  return (
    <div style={{ fontSize: 13 }}>
      {partes.length === 0
        ? <span>Batia: {fmt(contado)} onde o sistema apontava.</span>
        : <span><strong>Nao batia</strong> — {partes.join(' ')}. Estoque e prateleiras ja corrigidos.</span>}
      {rodape && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{rodape}</div>}
    </div>
  );
}

// ── A CONTAGEM CEGA ───────────────────────────────────────────────────────────────────────────
//
// Um campo por prateleira do sistema, VAZIO (nunca pre-preenchido: pre-preencher com zero faria
// "nao contei" parecer "contei zero"), e o "achei em outro lugar". O Gravar so libera com TODOS os
// campos preenchidos — prateleira vazia se escreve 0, de proposito.
function ContagemCega({ lugares, opcoesLugares, onCancelar, onEnviar }: {
  lugares: LugarCego[];
  opcoesLugares: OpcaoCombo[];
  onCancelar: () => void;
  onEnviar: (contados: { localizacao_id: number; quantidade: number }[]) => Promise<void>;
}) {
  const [valores, setValores] = useState<Record<number, string>>({});
  const [extras, setExtras] = useState<{ localizacao_id: number; codigo: string }[]>([]);
  const [escolhendo, setEscolhendo] = useState<string | number | ''>('');
  const [enviando, setEnviando] = useState(false);

  const todos = [...lugares.map((l) => ({ localizacao_id: l.localizacao_id, codigo: `${l.codigo}${l.principal ? ' ★' : ''}`, extra: false })),
    ...extras.map((e) => ({ ...e, extra: true }))];
  const usados = new Set(todos.map((t) => t.localizacao_id));
  const lista = opcoesLugares.map((o) => (usados.has(Number(o.value)) ? { ...o, desabilitada: true, nota: 'ja esta aqui' } : o));

  const faltando = todos.filter((t) => (valores[t.localizacao_id] ?? '').trim() === '');
  const invalido = todos.find((t) => {
    const v = (valores[t.localizacao_id] ?? '').trim();
    return v !== '' && (!Number.isFinite(num(v)) || num(v) < 0);
  });
  const falta = todos.length === 0 ? 'O produto nao tem prateleira cadastrada: acrescente onde voce achou (ou um lugar com 0, se nao achou).'
    : faltando.length > 0 ? `Falta contar: ${faltando.map((f) => f.codigo).join(', ')} (vazia = 0).`
    : invalido ? `Quantidade invalida em ${invalido.codigo}.`
    : null;

  function acrescentar(id: string | number | '') {
    if (id === '') return;
    const n = Number(id);
    if (usados.has(n)) return;
    const o = opcoesLugares.find((x) => Number(x.value) === n);
    setExtras((xs) => [...xs, { localizacao_id: n, codigo: String(o?.label ?? `#${n}`).split(' — ')[0] ?? `#${n}` }]);
    setEscolhendo('');
  }

  async function gravar() {
    if (falta || enviando) return;
    setEnviando(true);
    try {
      await onEnviar(todos.map((t) => ({ localizacao_id: t.localizacao_id, quantidade: num(valores[t.localizacao_id] ?? '0') })));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div style={{ borderTop: '1px solid var(--page-bg)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {todos.map((t) => (
          <div key={t.localizacao_id}>
            <label className="muted" style={{ fontSize: 11, display: 'block' }}>
              {t.codigo}{t.extra ? ' (achei aqui)' : ''}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                value={valores[t.localizacao_id] ?? ''}
                onChange={(e) => setValores((v) => ({ ...v, [t.localizacao_id]: e.target.value }))}
                inputMode="decimal"
                placeholder="quantos?"
                className="cel-num"
                style={{ width: 110, padding: '10px 12px', textAlign: 'right', fontSize: 18, fontWeight: 700 }}
              />
              {t.extra && (
                <button type="button" title="Tirar este lugar" onClick={() => setExtras((xs) => xs.filter((x) => x.localizacao_id !== t.localizacao_id))}
                  style={{ background: 'none', border: 0, cursor: 'pointer', color: 'inherit', padding: 4 }}>
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 420 }}>
        <Plus size={14} style={{ color: 'var(--primary)', flex: '0 0 auto' }} />
        <div style={{ flex: 1 }}>
          <ComboBox opcoes={lista} value={escolhendo} onChange={acrescentar} placeholder="Achei em outro lugar..." />
        </div>
      </div>

      {falta && <div style={{ fontSize: 13, color: 'var(--num-destaque)', fontWeight: 600 }}>{falta}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => void gravar()}
          disabled={!!falta || enviando}
          style={{
            flex: 2, padding: '14px 0', fontSize: 16, fontWeight: 700, borderRadius: 6, border: 'none',
            cursor: !falta && !enviando ? 'pointer' : 'not-allowed',
            background: !falta ? '#fde68a' : 'var(--page-bg)',
            color: !falta ? '#000' : 'var(--text-muted, #94a3b8)', whiteSpace: 'nowrap',
          }}
        >
          {enviando ? 'Gravando...' : 'Gravar contagem'}
        </button>
        <button
          type="button"
          className="btn-secundario"
          onClick={onCancelar}
          disabled={enviando}
          style={{ flex: 1, padding: '14px 0', fontSize: 15, whiteSpace: 'nowrap' }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ── O ACHADO (avaliacao avulsa) ───────────────────────────────────────────────────────────────
//
// *"Precisa tambem ter uma avaliacao avulsa, para pegar os casos de produtos encontrado em outras
// Localizacoes"* — e vale qualquer divergencia achada fora do sorteio, menos MT/RL/KG. Mesma
// contagem cega; se bater, nada se registra contra ninguem.
function Achado({ opcoesLugares }: { opcoesLugares: OpcaoCombo[] }) {
  const dialog = useDialog();
  const [produto, setProduto] = useState<ProdutoBuscado | null>(null);
  const [desfecho, setDesfecho] = useState<(Desfecho & { codigo: string }) | null>(null);

  async function enviar(contados: { localizacao_id: number; quantidade: number }[]) {
    if (!produto) return;
    try {
      const r = await api.post<Desfecho>('/api/estoque/auditoria/achado', { produto_id: produto.id, contados });
      setDesfecho({ ...r, codigo: produto.codigo_interno ?? '' });
      setProduto(null);
    } catch (e: any) {
      await dialog.alerta(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel gravar o achado.', { titulo: 'Nao gravou', tipo: 'aviso' });
    }
  }

  return (
    <div style={{ maxWidth: 860, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="muted" style={{ fontSize: 12 }}>
        Achou uma divergencia fora do sorteio — produto em outro lugar, quantidade diferente? Escolha o produto, conte
        todas as prateleiras dele e grave. Se nao bater, o estoque e' corrigido na hora e o achado <strong>pesa mais</strong> que um erro do sorteio na nota do estoquista (o peso esta no Premio do Estoquista). Produtos vendidos por metro, rolo ou quilo ficam fora.
      </div>

      {desfecho && (
        <div style={{ border: '1px solid var(--panel-border)', borderRadius: 6, padding: 12, background: 'var(--panel-bg)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <SeloDesfecho resultado={desfecho.resultado} />
          <div style={{ flex: 1 }}>
            <strong>{desfecho.codigo}</strong>{' '}
            {desfecho.resultado === 'certo'
              ? 'bateu — nada foi registrado.'
              : <ResumoDesfecho erroQtd={desfecho.erro_quantidade} erroLugar={desfecho.erro_lugar} sistema={desfecho.estoque_sistema} contado={desfecho.estoque_contado} />}
          </div>
          <button type="button" className="btn-secundario" onClick={() => setDesfecho(null)}>Ok</button>
        </div>
      )}

      {!produto ? (
        <BuscaProdutoEstoque endpoint="/api/estoque/auditoria/buscar" onEscolher={(p) => { setProduto(p); setDesfecho(null); }} bloquearForaDaAuditoria autoFocus />
      ) : (
        <div style={{ border: '1px solid var(--panel-border)', borderRadius: 6, padding: 12, background: 'var(--panel-bg)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Cabecalho produto={produto} lugaresTexto={produto.lugares.map((l) => `${l.codigo}${l.principal ? ' ★' : ''}`).join(' · ') || 'sem prateleira cadastrada'} />
          <ContagemCega lugares={produto.lugares} opcoesLugares={opcoesLugares} onCancelar={() => setProduto(null)} onEnviar={enviar} />
        </div>
      )}
    </div>
  );
}
